/**
 * Shared chevron navigation functionality for horizontal product sliders.
 * Used by: product-recommendations, custom-product-recommendations, 
 *          parts-and-maintenance, recently-viewed-products
 */
(function() {
  const SCROLL_AMOUNT = 360;
  
  /**
   * Initialize chevron navigation for a slider wrapper
   * @param {HTMLElement} sliderWrapper - The .tabbed-product-slider__slider-wrapper element
   * @param {Object} options - Optional configuration
   * @param {boolean} options.initHorizontalSlider - Whether to initialize horizontal slider (default: true)
   */
  const initSliderChevrons = (sliderWrapper, options = {}) => {
    if (!sliderWrapper || sliderWrapper.dataset.chevronsInitialized === 'true') return;
    
    sliderWrapper.dataset.chevronsInitialized = 'true';
    
    const { initHorizontalSlider = true } = options;
    
    const prevBtn = sliderWrapper.querySelector('[data-slider-prev]');
    const nextBtn = sliderWrapper.querySelector('[data-slider-next]');
    const viewport = sliderWrapper.querySelector('[data-horizontal-slider-viewport]');
    const track = sliderWrapper.querySelector('[data-horizontal-slider-track]');
    const slider = sliderWrapper.querySelector('[data-horizontal-slider]');
    
    if (!prevBtn || !nextBtn || !viewport) return;
    
    // Initialize horizontal slider scrollbar/thumb if needed
    if (initHorizontalSlider && slider && typeof window.initializeHorizontalSlider === 'function') {
      slider.dataset.horizontalSliderInitialized = 'false';
      window.initializeHorizontalSlider(slider);
    }
    
    const updateChevrons = () => {
      if (!track) return;
      
      const scrollLeft = viewport.scrollLeft;
      const maxScroll = track.scrollWidth - viewport.clientWidth;
      const hasOverflow = track.scrollWidth > viewport.clientWidth + 1;
      
      // Add/remove no-overflow class for border styling
      if (slider) {
        slider.classList.toggle('no-overflow', !hasOverflow);
      }
      
      if (hasOverflow) {
        prevBtn.classList.toggle('is-visible', scrollLeft > 5);
        nextBtn.classList.toggle('is-visible', scrollLeft < maxScroll - 5);
      } else {
        prevBtn.classList.remove('is-visible');
        nextBtn.classList.remove('is-visible');
      }
    };
    
    // Click handlers
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      viewport.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
    });
    
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      viewport.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
    });
    
    // Scroll handler
    viewport.addEventListener('scroll', updateChevrons);
    
    // Resize handler with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateChevrons, 100);
    };
    window.addEventListener('resize', handleResize);
    
    // Initial check
    setTimeout(updateChevrons, 200);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      sliderWrapper.dataset.chevronsInitialized = 'false';
    };
  };
  
  /**
   * Initialize all sliders matching the given selector within a container
   * @param {string} selector - CSS selector for slider containers
   * @param {HTMLElement} root - Root element to search within (default: document)
   * @param {Object} options - Options to pass to initSliderChevrons
   */
  const initAllSliders = (selector, root = document, options = {}) => {
    const containers = root.querySelectorAll(selector);
    containers.forEach(container => {
      const sliderWrapper = container.querySelector('.tabbed-product-slider__slider-wrapper');
      if (sliderWrapper) {
        initSliderChevrons(sliderWrapper, options);
      }
    });
  };
  
  // Expose globally
  window.ProductSliderChevrons = {
    init: initSliderChevrons,
    initAll: initAllSliders
  };
})();
